import { useTranslation } from "react-i18next";
import useEvent from "react-use-event-hook";
import { useRecoilValue, useSetRecoilState } from "recoil";

import {
  castErrorResult,
  ConfirmResult,
  createSuccessResultVoid,
  createSuccessResultVoidWithWarn,
  DirectionsServiceEnum,
  editFormDataAF,
  editFormDataInitialSnapshotAF,
  IRideDto,
  IRideRebuildPayload,
  isSoftHardValidationSingleErrorResponse,
  isSoftValidationSingleErrorResponse,
  mapHardValidationErrorToResult,
  RebuildRouteTypeEnum,
  resolveInitialRebuildRouteProvider,
  RideCrudModeKeyTuple,
  RidesApi,
  rideShouldOptimizeOnRebuild,
  SimpleResult,
  simpleResultErrorEmptyResponse,
  useSystemParams,
} from "@ryde/shared";

import { useSoftValidationModalConfirm } from "../use-soft-validation-modal-confirms";

export const useRideReBuild = () => {
  const { t } = useTranslation();
  const { confirmSoftValidationAction } = useSoftValidationModalConfirm();
  const systemParams = useSystemParams();
  const rideInitialSnapshot = useRecoilValue(
    editFormDataInitialSnapshotAF(RideCrudModeKeyTuple),
  );
  const setRideDtoGlobal = useSetRecoilState<Partial<IRideDto> | undefined>(
    editFormDataAF(RideCrudModeKeyTuple),
  );
  const setRideInitialSnapshot = useSetRecoilState<
    Partial<IRideDto> | undefined
  >(editFormDataInitialSnapshotAF(RideCrudModeKeyTuple));

  const rideRebuild = useEvent(
    async (rideDto: Partial<IRideDto>): Promise<SimpleResult<void>> => {
      const provider = resolveInitialRebuildRouteProvider({
        lastUsedProvider: rideInitialSnapshot?.metadata?.directionsService,
        systemDefaultProvider:
          systemParams.directionsService || DirectionsServiceEnum.Google,
      });

      const payload: IRideRebuildPayload = {
        ride: rideDto,
        rebuildType: RebuildRouteTypeEnum.RefreshCache,
        optimize: rideShouldOptimizeOnRebuild(rideDto),
        provider,
      };

      let rideRebuildResult = await RidesApi.rideRebuild(payload);

      if (isSoftValidationSingleErrorResponse(rideRebuildResult?.data)) {
        const softValidationResult = await confirmSoftValidationAction(
          rideRebuildResult.data,
        );

        if (softValidationResult === ConfirmResult.CANCEL) {
          return createSuccessResultVoid();
        }

        payload.token = rideRebuildResult.data.token;
        rideRebuildResult = await RidesApi.rideRebuild(payload);
      } else if (
        isSoftHardValidationSingleErrorResponse(rideRebuildResult?.data)
      ) {
        const hardValidationError = rideRebuildResult.data.validationErrors[0];
        return mapHardValidationErrorToResult<void>(hardValidationError);
      }

      if (!rideRebuildResult) {
        return simpleResultErrorEmptyResponse(t);
      }

      if (!rideRebuildResult.success) {
        return castErrorResult(rideRebuildResult);
      }

      const rebuiltRide = rideRebuildResult.data;
      setRideDtoGlobal(rebuiltRide);
      // Persist path: keep snapshot in sync so top-button default/tooltip match saved state.
      setRideInitialSnapshot(rebuiltRide);
      // Forward the notice when the rebuild left a part of the ride alone on purpose, so the caller can say so.
      return createSuccessResultVoidWithWarn(rideRebuildResult.warn);
    },
  );

  return { rideRebuild };
};
